import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { forwardRef, useEffect, useId, useRef, useState, } from 'react';
import { cn } from "../lib/cn.js";
import { Spinner } from "./feedback.js";
export const Combobox = forwardRef(({ value, onValueChange, items, search, onSearchChange, selectedLabel, loading = false, disabled = false, invalid = false, placeholder = 'Buscar…', emptyMessage = 'Nenhum resultado.', clearable = false, id, className, ...aria }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const listId = `${inputId}-list`;
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const rootRef = useRef(null);
    const listRef = useRef(null);
    // A results list that arrives shorter than the last one must not leave the
    // highlight pointing past its end, where Enter would choose nothing and
    // look broken.
    useEffect(() => {
        setActive((current) => (current >= items.length ? 0 : current));
    }, [items.length]);
    // Keep the highlighted row in view when the arrows walk past the fold.
    useEffect(() => {
        if (!open)
            return;
        const row = listRef.current?.querySelector(`[data-index="${String(active)}"]`);
        // Guarded rather than assumed: this is a convenience for a list longer
        // than its box, and jsdom does not implement it. A picker that throws
        // under test is worse than one that does not scroll.
        if (row instanceof HTMLElement &&
            typeof row.scrollIntoView === 'function') {
            row.scrollIntoView({ block: 'nearest' });
        }
    }, [active, open]);
    const close = () => {
        setOpen(false);
        onSearchChange('');
    };
    const choose = (item) => {
        if (item === undefined || item.disabled === true)
            return;
        onValueChange(item.value);
        setOpen(false);
        onSearchChange('');
    };
    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) {
                setOpen(true);
                return;
            }
            if (items.length === 0)
                return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive((current) => {
                const next = current + step;
                // Wraps, because a list short enough to walk off the end is short
                // enough that walking back round is faster than reversing.
                return next < 0 ? items.length - 1 : next % items.length;
            });
            return;
        }
        if (event.key === 'Home' && open) {
            event.preventDefault();
            setActive(0);
            return;
        }
        if (event.key === 'End' && open) {
            event.preventDefault();
            setActive(Math.max(0, items.length - 1));
            return;
        }
        if (event.key === 'Enter') {
            if (!open)
                return;
            // Only swallowed when it actually chooses something, so Enter still
            // submits the surrounding form when the popup is empty.
            if (items.length > 0) {
                event.preventDefault();
                choose(items[active]);
            }
            return;
        }
        if (event.key === 'Escape' && open) {
            // Not stopped from propagating when closed, so Esc still closes the
            // dialog this usually sits inside.
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key === 'Tab' && open)
            close();
    };
    const chosen = value !== null && value !== '';
    const activeId = open && items.length > 0 ? `${listId}-${String(active)}` : undefined;
    return (_jsxs("div", { ref: rootRef, className: cn('relative', className), onBlur: (event) => {
            // Only when focus leaves the whole control — clicking a result moves
            // focus inside it, and closing there would beat the click.
            if (!event.currentTarget.contains(event.relatedTarget))
                close();
        }, children: [_jsxs("div", { className: cn('flex h-(--control-height-md) w-full items-center gap-1.5 rounded-(--radius-md)', 'border border-(--border-default) bg-(--surface) px-2.5', 'transition-colors duration-(--duration-fast)', 'hover:border-(--border-strong)', 'focus-within:outline focus-within:outline-2 focus-within:outline-(--border-focus)', invalid ? 'border-(--danger)' : '', disabled
                    ? 'cursor-not-allowed bg-(--surface-sunken) opacity-60'
                    : ''), children: [_jsx(Search, { "aria-hidden": "true", className: "h-3.5 w-3.5 shrink-0 text-(--text-muted)" }), _jsx("input", { ref: ref, id: inputId, type: "text", role: "combobox", autoComplete: "off", disabled: disabled, "aria-expanded": open, "aria-controls": open ? listId : undefined, "aria-activedescendant": activeId, "aria-autocomplete": "list", "aria-invalid": invalid || undefined, "aria-describedby": aria['aria-describedby'], value: open ? search : (selectedLabel ?? ''), placeholder: chosen && !open ? undefined : placeholder, onChange: (event) => {
                            if (!open)
                                setOpen(true);
                            setActive(0);
                            onSearchChange(event.target.value);
                        }, onFocus: () => {
                            if (!disabled)
                                setOpen(true);
                        }, onKeyDown: onKeyDown, className: cn('h-full w-full min-w-0 border-0 bg-transparent p-0 outline-none', 'text-(length:--text-sm) text-(--text-primary)', 'placeholder:text-(--text-muted) disabled:cursor-not-allowed') }), loading ? _jsx(Spinner, { className: "h-3.5 w-3.5 shrink-0" }) : null, clearable && chosen && !disabled ? (_jsx("button", { type: "button", "aria-label": "Limpar sele\u00E7\u00E3o", onClick: () => {
                            onValueChange(null);
                            onSearchChange('');
                        }, className: cn('shrink-0 rounded-(--radius-sm) p-0.5 text-(--text-muted)', 'hover:bg-(--surface-hover) hover:text-(--text-primary)'), children: _jsx(X, { "aria-hidden": "true", className: "h-3.5 w-3.5" }) })) : null, _jsx(ChevronDown, { "aria-hidden": "true", className: cn('h-3.5 w-3.5 shrink-0 text-(--text-muted) transition-transform', open ? 'rotate-180' : '') })] }), open ? (_jsx("ul", { ref: listRef, id: listId, role: "listbox", className: cn('absolute z-(--z-dropdown) mt-1 max-h-64 w-full overflow-y-auto', 'rounded-(--radius-md) border border-(--border-default)', 'bg-(--surface-raised) py-1 shadow-(--shadow-md)'), children: items.length === 0 ? (_jsx("li", { 
                    // Not a listbox option: it is not selectable, and announcing it
                    // as one would tell a screen-reader user there is a choice.
                    className: "px-3 py-2 text-(length:--text-sm) text-(--text-muted)", children: loading ? 'Buscando…' : emptyMessage })) : (items.map((item, index) => (_jsxs("li", { id: `${listId}-${String(index)}`, role: "option", "data-index": index, "aria-selected": item.value === value, "aria-disabled": item.disabled === true || undefined, 
                    // Beats the input's blur, so the click still lands.
                    onMouseDown: (event) => {
                        event.preventDefault();
                    }, onMouseEnter: () => {
                        setActive(index);
                    }, onClick: () => {
                        choose(item);
                    }, className: cn('flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5', 'text-(length:--text-sm) text-(--text-primary)', index === active ? 'bg-(--surface-hover)' : '', item.disabled === true
                        ? 'cursor-not-allowed opacity-50'
                        : ''), children: [_jsxs("span", { className: "flex min-w-0 flex-col", children: [_jsx("span", { className: "truncate", children: item.label }), item.hint === undefined ? null : (_jsx("span", { className: "truncate text-(length:--text-xs) text-(--text-muted)", children: item.hint }))] }), item.value === value ? (_jsx(Check, { "aria-hidden": "true", className: "h-3.5 w-3.5 shrink-0 text-(--brand-primary)" })) : null] }, item.value)))) })) : null] }));
});
Combobox.displayName = 'Combobox';
//# sourceMappingURL=combobox.js.map